/**
 * Read accounting for Firestore access.
 *
 * Firestore bills per document returned from the server, and read cost is
 * invisible in development until it shows up as a bill or a quota wall — this
 * app already hit that once. These wrappers make the cost of a page or an
 * interaction observable (see the "Firestore reads" DEV drawer section) and
 * assertable in tests ("opening Collection performs exactly one read").
 *
 * Billing model, mirrored as closely as the SDK allows:
 * - Cache-served snapshots cost nothing, so `fromCache` results record 0.
 * - An empty server query still bills one read.
 * - A listener bills for the documents it is sent, so the first snapshot costs
 *   its full size and later ones cost only their changes.
 *
 * Counting is always on: it is a `Map` increment on an operation that just did
 * network I/O. Only the UI is dev-gated.
 */

import {
  getDoc,
  getDocs,
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';

export type FirestoreReadKind = 'getDoc' | 'getDocs' | 'snapshot';

export interface FirestoreReadTally {
  /** Path with document ids replaced by `{id}`, e.g. `users/{id}/data/collection`. */
  label: string;
  kind: FirestoreReadKind;
  /** Billed document reads, excluding cache-served results. */
  reads: number;
  /** Times the call was made, including cache-served ones. */
  calls: number;
}

type Listener = () => void;

const tallies = new Map<string, FirestoreReadTally>();
const listeners = new Set<Listener>();

function notify() {
  for (const listener of listeners) listener();
}

/**
 * Document ids that are fixed names rather than per-user keys. Masking these
 * would collapse the paths we most want to tell apart — the collection bag
 * lives at `users/{uid}/data/collection`.
 */
const STATIC_DOC_IDS = new Set(['collection']);

/**
 * Collapse a concrete path to a shape. Firestore paths alternate
 * collection/document, so odd segments are ids.
 */
export function readLabelForPath(path: string): string {
  return path
    .split('/')
    .map((segment, index) =>
      index % 2 === 1 && !STATIC_DOC_IDS.has(segment) ? '{id}' : segment,
    )
    .join('/');
}

export function recordFirestoreRead(
  label: string,
  kind: FirestoreReadKind,
  reads: number,
): void {
  const key = `${kind}:${label}`;
  const existing = tallies.get(key);
  if (existing) {
    existing.reads += reads;
    existing.calls += 1;
  } else {
    tallies.set(key, { label, kind, reads, calls: 1 });
  }
  notify();
}

/** Tallies, most expensive first. */
export function getFirestoreReadStats(): FirestoreReadTally[] {
  return [...tallies.values()].sort((a, b) => b.reads - a.reads || b.calls - a.calls);
}

export function getFirestoreReadTotal(): number {
  let total = 0;
  for (const tally of tallies.values()) total += tally.reads;
  return total;
}

export function resetFirestoreReadStats(): void {
  tallies.clear();
  notify();
}

export function subscribeToFirestoreReads(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `getDoc` that records what it cost. */
export async function countedGetDoc<T = DocumentData>(
  ref: DocumentReference<T>,
): Promise<DocumentSnapshot<T>> {
  const snap = await getDoc(ref);
  recordFirestoreRead(
    readLabelForPath(ref.path),
    'getDoc',
    snap.metadata.fromCache ? 0 : 1,
  );
  return snap;
}

/**
 * `getDocs` that records what it cost. `label` is explicit because a `Query`
 * does not expose its path.
 */
export async function countedGetDocs<T = DocumentData>(
  label: string,
  query: Query<T>,
): Promise<QuerySnapshot<T>> {
  const snap = await getDocs(query);
  // An empty server response still bills one read.
  recordFirestoreRead(label, 'getDocs', snap.metadata.fromCache ? 0 : Math.max(snap.size, 1));
  return snap;
}

/**
 * `onSnapshot` over a query that records each delivered snapshot. The first
 * costs the full result; later ones cost only what changed.
 */
export function countedOnSnapshot<T = DocumentData>(
  label: string,
  query: Query<T>,
  onNext: (snap: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  let first = true;
  return onSnapshot(
    query,
    (snap) => {
      if (!snap.metadata.fromCache) {
        const billed = first ? Math.max(snap.size, 1) : snap.docChanges().length;
        if (billed > 0) recordFirestoreRead(label, 'snapshot', billed);
      }
      first = false;
      onNext(snap);
    },
    onError,
  );
}

/** `onSnapshot` over a single document that records each delivered snapshot. */
export function countedOnDocSnapshot<T = DocumentData>(
  ref: DocumentReference<T>,
  onNext: (snap: DocumentSnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  const label = readLabelForPath(ref.path);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.metadata.fromCache) recordFirestoreRead(label, 'snapshot', 1);
      onNext(snap);
    },
    onError,
  );
}
